'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, User, Lock, Check, X, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FieldErrors {
  [key: string]: string;
}

interface Touched {
  [key: string]: boolean;
}

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: string;
  label: string;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError, initiateGoogleAuth, user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [preFilledFields, setPreFilledFields] = useState<{[key: string]: boolean}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Pre-fill form data if user is partially authenticated (e.g., from Google Auth)
  useEffect(() => {
    if (user && user.email) {
      const nameParts = user.name ? user.name.split(' ') : [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormData(prev => ({
        ...prev,
        email: user.email,
        firstName: firstName,
        lastName: lastName
      }));

      setPreFilledFields({
        email: true,
        firstName: !!firstName,
        lastName: !!lastName
      });
    }
  }, [user]);

  // Password strength checker
  const checkPasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    const feedback: string[] = [];

    if (password.length >= 8) score++;
    else feedback.push('At least 8 characters');

    if (/[a-z]/.test(password)) score++;
    else feedback.push('One lowercase letter');

    if (/[A-Z]/.test(password)) score++;
    else feedback.push('One uppercase letter');

    if (/\d/.test(password)) score++;
    else feedback.push('One number');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    else feedback.push('One special character');

    // Bonus point for very strong passwords (12+ characters)
    if (password.length >= 12) score++;

    const colors = ['bg-red-500', 'bg-red-400', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500', 'bg-green-600'];
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];

    // Ensure we don't go out of bounds (0-5 range for 6 levels)
    const safeScore = Math.min(Math.max(score, 0), 5);

    return {
      score: safeScore,
      feedback,
      color: colors[safeScore],
      label: labels[safeScore]
    };
  };

  const passwordStrength = checkPasswordStrength(formData.password);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':
        return !value.trim() ? 'First name is required' : '';
      case 'lastName':
        return !value.trim() ? 'Last name is required' : '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';

        // Enhanced password validation
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
          return 'Password must contain uppercase, lowercase, number and special character';
        }

        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData[fieldName as keyof FormData]);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Don't allow changes to pre-filled fields
    if (preFilledFields[name]) {
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Clear global error when user starts typing
    if (error) {
      clearError();
    }

    // Real-time validation for email and confirm password
    if (name === 'email' && touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }

    if (name === 'confirmPassword' && touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (isSubmitting || isLoading) {
      console.log('⚠️ Form submission already in progress');
      return;
    }

    setSubmitAttempted(true);
    setIsSubmitting(true);

    // Clear any existing errors
    clearError();
    setFieldErrors({});

    // Validate all fields
    const errors: FieldErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof FormData]);
      if (error) errors[key] = error;
    });

    // Mark all fields as touched
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true
    });

    setFieldErrors(errors);

    // Don't proceed if there are validation errors
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form validation failed:', errors);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('📝 Submitting registration form...');

      // Call the register function from AuthContext
      const fullName = `${formData.firstName} ${formData.lastName}`;
      await register(formData.email, formData.password, fullName);

      console.log('✅ Registration successful');
      // The redirect to onboarding is handled in AuthContext
    } catch (error: any) {
      console.error('❌ Signup error:', error);

      // Handle specific password validation errors
      if (error.message && error.message.includes('Password must contain')) {
        setFieldErrors(prev => ({
          ...prev,
          password: error.message
        }));

        // Also mark password field as touched to show the error
        setTouched(prev => ({
          ...prev,
          password: true
        }));
      }

      // Error is also handled by AuthContext and displayed via error state
      // But we don't re-throw to prevent unhandled promise rejection
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white relative overflow-hidden">
      {/* Sophisticated Background Texture */}
      <div className="absolute inset-0 z-0">
        {/* Base gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/10 via-gray-600/5 to-gray-400/8 dark:from-gray-100/8 dark:via-gray-300/5 dark:to-gray-500/10"></div>

        {/* Visible dot pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.08] dark:opacity-[0.12]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dot-pattern" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="16" cy="16" r="1.5" fill="currentColor" />
              <circle cx="8" cy="8" r="0.8" fill="currentColor" opacity="0.6" />
              <circle cx="24" cy="24" r="0.8" fill="currentColor" opacity="0.6" />
              <circle cx="8" cy="24" r="0.5" fill="currentColor" opacity="0.4" />
              <circle cx="24" cy="8" r="0.5" fill="currentColor" opacity="0.4" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dot-pattern)" />
        </svg>

        {/* Geometric grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.06] dark:opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M0 24 L48 24" stroke="currentColor" strokeWidth="0.5" opacity="0.5" />
              <path d="M24 0 L24 48" stroke="currentColor" strokeWidth="0.5" opacity="0.5" />
              <path d="M0 0 L48 48" stroke="currentColor" strokeWidth="0.25" opacity="0.3" />
              <path d="M48 0 L0 48" stroke="currentColor" strokeWidth="0.25" opacity="0.3" />
              <circle cx="24" cy="24" r="2" fill="none" stroke="currentColor" strokeWidth="0.25" opacity="0.2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>

        {/* Paper texture */}
        <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }}></div>

        {/* Subtle hexagon pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03] dark:opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hex-pattern" width="60" height="52" patternUnits="userSpaceOnUse">
              <polygon points="30,2 50,15 50,37 30,50 10,37 10,15" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
              <polygon points="0,28 20,41 20,63 0,76 -20,63 -20,41" fill="none" stroke="currentColor" strokeWidth="0.25" opacity="0.2" />
              <polygon points="60,28 80,41 80,63 60,76 40,63 40,41" fill="none" stroke="currentColor" strokeWidth="0.25" opacity="0.2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex-pattern)" />
        </svg>

        {/* Radial gradient for depth */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-gray-900/[0.05] dark:to-gray-100/[0.05]"></div>

        {/* Edge fade gradients */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
        <div className="absolute top-0 bottom-0 left-0 w-24 bg-gradient-to-r from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
        <div className="absolute top-0 bottom-0 right-0 w-24 bg-gradient-to-l from-gray-50 dark:from-gray-950 to-transparent z-10"></div>
      </div>

      {/* Left side - Brand/Logo */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-12 relative z-10">
        <div className="max-w-md w-full space-y-8 animate-fadeIn">
          {/* Logo - Always visible */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="h-16 w-16 rounded-xl bg-gray-900 dark:bg-gray-100 flex items-center justify-center text-white dark:text-gray-900 font-bold text-2xl shadow-lg animate-fadeInScale">
                M
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold">BIZAMPLY</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Analytics Platform</p>
          </div>

          {/* Marketing content - Hidden on mobile */}
          <div className="hidden md:block mt-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Secure Account Creation</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Your data is protected with enterprise-grade security and encryption.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Quick Setup</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Get started in minutes with our streamlined onboarding process.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900">
                    <Check className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">24/7 Support</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Our team is here to help you succeed every step of the way.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Sign Up Form */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-4 md:p-8 lg:p-12 relative z-10">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold">Create Account</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Join thousands of businesses using Bizamply
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Pre-filled Info Message */}
            {(preFilledFields.email || preFilledFields.firstName || preFilledFields.lastName) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Some information has been pre-filled from your account. You can only edit the remaining fields.
                  </p>
                </div>
              </div>
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={() => handleFieldBlur('firstName')}
                    readOnly={preFilledFields.firstName}
                    className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                      preFilledFields.firstName
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600'
                        : fieldErrors.firstName && touched.firstName
                        ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                    }`}
                    placeholder="John"
                  />
                  {preFilledFields.firstName && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Check className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                </div>
                {fieldErrors.firstName && touched.firstName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={() => handleFieldBlur('lastName')}
                    readOnly={preFilledFields.lastName}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                      preFilledFields.lastName
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600'
                        : fieldErrors.lastName && touched.lastName
                        ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                    }`}
                    placeholder="Doe"
                  />
                  {preFilledFields.lastName && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Check className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                </div>
                {fieldErrors.lastName && touched.lastName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('email')}
                  readOnly={preFilledFields.email}
                  className={`pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    preFilledFields.email
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600 pr-10'
                      : fieldErrors.email && touched.email
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-red-500 focus:ring-red-500'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                  }`}
                  placeholder="you@example.com"
                />
                {preFilledFields.email && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Check className="h-4 w-4 text-green-500" />
                  </div>
                )}
              </div>
              {fieldErrors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('password')}
                  className={`pl-10 pr-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                    fieldErrors.password && touched.password
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                  }`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium">{passwordStrength.label}</span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Missing: {passwordStrength.feedback.join(', ')}
                    </div>
                  )}
                </div>
              )}

              {fieldErrors.password && touched.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('confirmPassword')}
                  className={`pl-10 pr-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                    fieldErrors.confirmPassword && touched.confirmPassword
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-700 focus:ring-gray-300 dark:focus:ring-gray-600'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {fieldErrors.confirmPassword && touched.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            {/* Error Display */}
            {(error || (submitAttempted && Object.keys(fieldErrors).length > 0)) && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
                )}
                {submitAttempted && Object.keys(fieldErrors).length > 0 && (
                  <div className="text-sm text-red-600 dark:text-red-400">
                    <p className="font-medium mb-1">Please fix the following errors:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(fieldErrors).map(([field, error]) => (
                        error && <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform active:scale-95"
              >
                {(isLoading || isSubmitting) ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900 mr-2"></div>
                    {isSubmitting ? 'Creating Account...' : 'Loading...'}
                  </div>
                ) : (
                  <div className="flex items-center">
                    Create Account
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Auth Button */}
            <div>
              <button
                type="button"
                onClick={initiateGoogleAuth}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-700 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
